import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { resolveFrontendApiBase } from '../utils/apiBase';
import axios from 'axios'
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Divider,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  List,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import { getAuth } from 'firebase/auth'

import { getAllLanguages, getLanguageDisplayName, getLanguageDisplayText } from '../LanguageConfig'
import type { ColumnsType } from 'antd/es/table'
import type {
  SurveyResponseSummary,
  SurveyResponseDetail,
  QuestionAnswer,
} from '../types/survey'

import { useNavigate } from 'react-router-dom';
import './LanguageSurveyAdmin.css';

const { Text, Paragraph } = Typography

const API_BASE = resolveFrontendApiBase()

type QuestionType =
  | 'shortText'
  | 'longText'
  | 'number'
  | 'multiSelect'
  | 'singleSelect'
  | 'rating'
  | 'boolean'

type AdminScope = 'general' | 'language' | 'language_common'

type ScopeValue = 'general' | 'language_common' | string

interface SurveyQuestionRecord {
  questionId: string
  prompt: string
  description?: string | null
  type: QuestionType
  options?: string[]
  isRequired: boolean
  defaultValue?: any
  active: boolean
  helpText?: string | null
}

interface QuestionSetResponse {
  order: string[]
  questions: SurveyQuestionRecord[]
  updatedAt?: string | null
}

interface QuestionSetsResponse {
  general: QuestionSetResponse
  languageCommon: QuestionSetResponse
  languages: Record<string, QuestionSetResponse>
}

interface QuestionFormValues {
  prompt: string
  description?: string
  type: QuestionType
  options?: string[]
  isRequired: boolean
  defaultValue?: any
  active: boolean
  helpText?: string
}

const SELECT_TYPES: QuestionType[] = ['multiSelect', 'singleSelect']



const getAdminScope = (value: ScopeValue): AdminScope => {
  if (value === 'general') {
    return 'general'
  }
  if (value === 'language_common') {
    return 'language_common'
  }
  return 'language'
}

const coerceDefaultForForm = (type: QuestionType, value: any) => {
  if (value === undefined || value === null) {
    return undefined
  }

  if (type === 'boolean') {
    return Boolean(value)
  }

  if (type === 'number' || type === 'rating') {
    const num = typeof value === 'number' ? value : Number(value)
    return Number.isFinite(num) ? num : undefined
  }

  if (type === 'multiSelect') {
    if (Array.isArray(value)) {
      return value.map((entry) => String(entry))
    }
    if (typeof value === 'string') {
      return [value]
    }
    return undefined
  }

  if (type === 'singleSelect') {
    if (typeof value === 'string') {
      return value
    }
    if (Array.isArray(value) && value.length > 0) {
      return String(value[0])
    }
    return undefined
  }

  return String(value)
}

const prepareDefaultForPayload = (type: QuestionType, value: any) => {
  if (value === undefined || value === null) {
    return undefined
  }

  if (type === 'multiSelect') {
    if (!Array.isArray(value)) {
      return undefined
    }
    const cleaned = value.map((entry) => String(entry).trim()).filter(Boolean)
    return cleaned.length > 0 ? cleaned : undefined
  }

  if (type === 'singleSelect') {
    const str = String(value).trim()
    return str ? str : undefined
  }

  if (type === 'number' || type === 'rating') {
    const num = typeof value === 'number' ? value : Number(value)
    return Number.isFinite(num) ? num : undefined
  }

  if (type === 'boolean') {
    return Boolean(value)
  }

  const str = String(value).trim()
  return str ? str : undefined
}

const normalizeQuestion = (question: SurveyQuestionRecord): SurveyQuestionRecord => ({
  ...question,
  description: question.description ?? undefined,
  helpText: question.helpText ?? undefined,
  options: question.options?.map((option) => String(option)),
  defaultValue: coerceDefaultForForm(question.type, question.defaultValue),
})

const LanguageSurveyAdmin: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'questions' | 'responses'>('questions')
  const [selectedScope, setSelectedScope] = useState<ScopeValue>('general')
  const [questions, setQuestions] = useState<SurveyQuestionRecord[]>([])
  const [updatedAt, setUpdatedAt] = useState<string | null>(null)
  const [questionError, setQuestionError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [reordering, setReordering] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [modalVisible, setModalVisible] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editingQuestion, setEditingQuestion] = useState<SurveyQuestionRecord | null>(null)
  const [responses, setResponses] = useState<SurveyResponseSummary[]>([])
  const [responsesError, setResponsesError] = useState<string | null>(null)
  const [responsesLoading, setResponsesLoading] = useState(false)
  const [responsesNextCursor, setResponsesNextCursor] = useState<string | null>(null)
  const [responseLanguageFilter, setResponseLanguageFilter] = useState<string | null>(null)
  const [responseStatusFilter, setResponseStatusFilter] = useState<string | null>(null)
  const [detailVisible, setDetailVisible] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)
  const [responseDetail, setResponseDetail] = useState<SurveyResponseDetail | null>(null)
  const [responseDeletingId, setResponseDeletingId] = useState<string | null>(null)
  const [copyModalVisible, setCopyModalVisible] = useState(false)
  const [copySourceQuestion, setCopySourceQuestion] = useState<SurveyQuestionRecord | null>(null)
  const [copySelectedLanguages, setCopySelectedLanguages] = useState<string[]>([])
  const [copySubmitting, setCopySubmitting] = useState(false)
  const [exportingCsv, setExportingCsv] = useState(false)
  const [form] = Form.useForm<QuestionFormValues>()
  const navigate = useNavigate();
  const allLanguages = useMemo(() => getAllLanguages(true), [])

  const currentScope = getAdminScope(selectedScope)
  const currentLanguage = currentScope === 'language' ? selectedScope : undefined
  const pageSize = 25

  const extractAdminError = useCallback((error: any, fallback: string) => (
    error?.response?.data?.detail || error?.message || fallback
  ), [])

  const formatTimestamp = useCallback((value?: string | null) => {
    if (!value) {
      return '—'
    }
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) {
      return value
    }
    return date.toLocaleString()
  }, [])

  const formatAnswerValue = useCallback((value: any) => {
    if (value === null || value === undefined || value === '') {
      return '—'
    }
    if (Array.isArray(value)) {
      return value.join(', ')
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No'
    }
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value, null, 2)
      } catch (err) {
        return String(value)
      }
    }
    return String(value)
  }, [])

  const responseColumns = useMemo<ColumnsType<SurveyResponseSummary>>(
    () => [
      {
        title: 'User',
        dataIndex: 'userId',
        key: 'userId',
        render: (value: string) => <Text strong>{value}</Text>,
      },
      {
        title: 'Languages',
        key: 'languages',
        render: (_, record) => (
          <Space size={4}>
            <Tag color="blue">{record.selectedLanguages.length}</Tag>
            <Text type="secondary">{record.selectedLanguages.join(', ') || 'None'}</Text>
          </Space>
        ),
      },
      {
        title: 'Progress',
        key: 'progress',
        render: (_, record) => (
          <Text>{`${record.answeredLanguages}/${record.totalLanguages}`}</Text>
        ),
      },
      {
        title: 'Completed Languages',
        dataIndex: 'completedLanguages',
        key: 'completedLanguages',
        render: (value: number, record) => (
          <Text>{`${value}/${record.totalLanguages}`}</Text>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (value: string | null | undefined, record) => (
          <Tag color={record.isComplete ? 'green' : 'orange'}>
            {(value || record.isComplete ? 'completed' : 'in_progress').toUpperCase()}
          </Tag>
        ),
      },
      {
        title: 'Updated',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        render: formatTimestamp,
      },
    ],
    [formatTimestamp]
  )

  const getAuthHeaders = useCallback(async () => {
    const auth = getAuth()
    const user = auth.currentUser
    if (!user) {
      throw new Error('You must be signed in as an admin to manage survey questions.')
    }
    const token = await user.getIdToken()
    return { Authorization: `Bearer ${token}` }
  }, [])

  const fetchQuestions = useCallback(async () => {
    setLoading(true)
    setQuestionError(null)
    try {
      const headers = await getAuthHeaders()
      const params = currentScope === 'language' && currentLanguage
        ? { language: currentLanguage }
        : undefined
      const { data } = await axios.get<QuestionSetsResponse>(
        `${API_BASE}/surveys/language/admin/questions`,
        {
          headers,
          params,
        }
      )

      let setData: QuestionSetResponse
      if (currentScope === 'general') {
        setData = data.general
      } else if (currentScope === 'language_common') {
        setData = data.languageCommon
      } else {
        setData = data.languages[currentLanguage || ''] || { order: [], questions: [] }
      }

      const order = setData.order || []
      const questionMap = new Map(
        (setData.questions || []).map((record) => [record.questionId, normalizeQuestion(record)])
      )

      const orderedQuestions = order.length
        ? order
            .map((id) => questionMap.get(id))
            .filter((entry): entry is SurveyQuestionRecord => Boolean(entry))
        : Array.from(questionMap.values())

      const extras = (setData.questions || [])
        .filter((record) => !order.includes(record.questionId))
        .map((record) => normalizeQuestion(record))

      setQuestions([...orderedQuestions, ...extras])
      setUpdatedAt(setData.updatedAt ?? null)
    } catch (error) {
      console.error('Failed to load survey questions', error)
      const detail = extractAdminError(error, 'Unable to load survey questions. Please try again.')
      setQuestionError(detail)
      message.error(detail)
    } finally {
      setLoading(false)
    }
  }, [currentLanguage, currentScope, extractAdminError, getAuthHeaders])

  const fetchResponses = useCallback(
    async ({ cursor, append }: { cursor?: string | null; append?: boolean } = {}) => {
      setResponsesLoading(true)
      setResponsesError(null)
      try {
        const headers = await getAuthHeaders()
        const params: Record<string, any> = { limit: pageSize }
        if (cursor) {
          params.cursor = cursor
        }
        if (responseLanguageFilter) {
          params.language = responseLanguageFilter
        }
        if (responseStatusFilter) {
          params.status = responseStatusFilter
        }
        const { data } = await axios.get<{
          items: SurveyResponseSummary[]
          nextCursor?: string | null
        }>(`${API_BASE}/surveys/language/admin/responses`, {
          headers,
          params,
        })
        const items = data.items || []
        setResponses((prev) => (append ? [...prev, ...items] : items))
        setResponsesNextCursor(data.nextCursor || null)
      } catch (error) {
        console.error('Failed to load survey responses', error)
        const detail = extractAdminError(error, 'Unable to load survey responses. Please try again.')
        setResponsesError(detail)
        message.error(detail)
      } finally {
        setResponsesLoading(false)
      }
    },
    [
      getAuthHeaders,
      extractAdminError,
      pageSize,
      responseLanguageFilter,
      responseStatusFilter,
    ]
  )

  const handleLoadMoreResponses = useCallback(() => {
    if (!responsesNextCursor) {
      return
    }
    fetchResponses({ cursor: responsesNextCursor, append: true })
  }, [fetchResponses, responsesNextCursor])

  const openResponseDetail = useCallback(
    async (responseId: string) => {
      setDetailVisible(true)
      setDetailLoading(true)
      setDetailError(null)
      setResponseDetail(null)
      try {
        const headers = await getAuthHeaders()
        const { data } = await axios.get<SurveyResponseDetail>(
          `${API_BASE}/surveys/language/admin/responses/${responseId}`,
          { headers }
        )
        setResponseDetail(data)
      } catch (error) {
        console.error('Failed to load response detail', error)
        const detail = extractAdminError(error, 'Unable to load response detail. Please try again.')
        setDetailError(detail)
        message.error(detail)
      } finally {
        setDetailLoading(false)
      }
    },
    [extractAdminError, getAuthHeaders]
  )

  const handleDeleteResponse = useCallback(
    async (responseId: string) => {
      setResponseDeletingId(responseId)
      try {
        const headers = await getAuthHeaders()
        await axios.delete(`${API_BASE}/surveys/language/admin/responses/${responseId}`, {
          headers,
        })
        message.success('Survey response deleted')
        setDetailVisible(false)
        setResponseDetail(null)
        setResponses((prev) => prev.filter((response) => response.responseId !== responseId))
        setResponsesNextCursor(null)
        await fetchResponses()
      } catch (error) {
        console.error('Failed to delete survey response', error)
        const detail = extractAdminError(error, 'Unable to delete survey response. Please try again.')
        setDetailError(detail)
        message.error(detail)
      } finally {
        setResponseDeletingId(null)
      }
    },
    [extractAdminError, fetchResponses, getAuthHeaders]
  )

  useEffect(() => {
    fetchQuestions()
  }, [fetchQuestions])

  useEffect(() => {
    if (activeTab === 'responses') {
      setResponses([])
      setResponsesNextCursor(null)
      fetchResponses()
    }
  }, [activeTab, fetchResponses, responseLanguageFilter, responseStatusFilter])

  const handleScopeChange = (value: ScopeValue) => {
    setSelectedScope(value)
  }

  const handleResponseLanguageChange = useCallback((value: string | undefined) => {
    setResponseLanguageFilter(value || null)
  }, [])

  const handleResponseStatusChange = useCallback((value: string | undefined) => {
    setResponseStatusFilter(value || null)
  }, [])

  const handleRefreshResponses = useCallback(() => {
    fetchResponses()
  }, [fetchResponses])

  const handleDownloadResponsesCsv = useCallback(async () => {
    setExportingCsv(true)
    try {
      const headers = await getAuthHeaders()
      let cursor: string | null = null
      const summaries: SurveyResponseSummary[] = []
      do {
        const params: Record<string, any> = { limit: 100 }
        if (cursor) {
          params.cursor = cursor
        }
        if (responseLanguageFilter) {
          params.language = responseLanguageFilter
        }
        if (responseStatusFilter) {
          params.status = responseStatusFilter
        }
        const { data } = await axios.get<{
          items: SurveyResponseSummary[]
          nextCursor?: string | null
        }>(`${API_BASE}/surveys/language/admin/responses`, {
          headers,
          params,
        })
        if (data.items?.length) {
          summaries.push(...data.items)
        }
        cursor = data.nextCursor || null
      } while (cursor)

      if (summaries.length === 0) {
        message.info('No survey responses to export.')
        return
      }

      const details: SurveyResponseDetail[] = []
      let failedCount = 0
      const batchSize = 5
      for (let index = 0; index < summaries.length; index += batchSize) {
        const batch = summaries.slice(index, index + batchSize)
        const results = await Promise.allSettled(
          batch.map((summary) =>
            axios
              .get<SurveyResponseDetail>(
                `${API_BASE}/surveys/language/admin/responses/${summary.responseId}`,
                { headers }
              )
              .then((response) => response.data)
          )
        )
        results.forEach((result) => {
          if (result.status === 'fulfilled') {
            details.push(result.value)
          } else {
            failedCount += 1
            console.error('Failed to fetch response detail for export', result.reason)
          }
        })
      }

      if (details.length === 0) {
        setResponsesError('Unable to export survey responses.')
        message.error('Unable to export survey responses.')
        return
      }

      const formatScopeLabel = (scope: QuestionAnswer['scope']) => {
        switch (scope) {
          case 'general':
            return 'General'
          case 'language_common':
            return 'Shared Language'
          case 'language':
            return 'Language Specific'
          default:
            return scope
        }
      }

      const rows: string[][] = []
      details.forEach((detail) => {
        const summary = detail.summary
        const selectedLanguagesText = summary.selectedLanguages.length
          ? summary.selectedLanguages.map((lang) => getLanguageDisplayText(lang)).join(', ')
          : '—'
        const baseFields: string[] = [
          summary.responseId || '—',
          summary.userId || '—',
          summary.status || (summary.isComplete ? 'completed' : 'in_progress'),
          summary.updatedAt ? new Date(summary.updatedAt).toISOString() : '—',
          selectedLanguagesText,
        ]

        const pushAnswerRow = (answer: QuestionAnswer, languageLabel: string) => {
          const prompt = answer.prompt || answer.questionId || '—'
          const answerText = formatAnswerValue(answer.answer)
          rows.push([
            ...baseFields,
            formatScopeLabel(answer.scope),
            languageLabel,
            answer.questionId || '—',
            String(prompt ?? '—'),
            answerText,
          ])
        }

        detail.general.forEach((answer) => {
          pushAnswerRow(answer, '')
        })

        detail.languages.forEach((languageBlock) => {
          const languageLabel = getLanguageDisplayText(languageBlock.language)
          languageBlock.shared.forEach((answer) => {
            pushAnswerRow(answer, languageLabel)
          })
          languageBlock.specific.forEach((answer) => {
            pushAnswerRow(answer, languageLabel)
          })
        })
      })

      if (rows.length === 0) {
        message.info('No answers found to export.')
        return
      }

      const header = [
        'Response ID',
        'User ID',
        'Status',
        'Updated At',
        'Selected Languages',
        'Question Scope',
        'Language',
        'Question ID',
        'Question Prompt',
        'Answer',
      ]

      const escapeCsvValue = (value: string | null | undefined) => {
        const normalized = (value ?? '').replace(/\r?\n/g, '\n').replace(/"/g, '""')
        return `"${normalized}"`
      }

      const csvLines = [
        header.map(escapeCsvValue).join(','),
        ...rows.map((row) => row.map((field) => escapeCsvValue(field)).join(',')),
      ]

      const csvContent = '\ufeff' + csvLines.join('\n') // Prepend BOM for Excel compatibility
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `language-survey-results-${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      if (failedCount > 0) {
        message.warning(`Exported with ${failedCount} response${failedCount === 1 ? '' : 's'} skipped.`)
      } else {
        message.success(`Downloaded ${rows.length} answers from ${details.length} responses.`)
      }
    } catch (error) {
      console.error('Failed to export survey responses', error)
      const detail = extractAdminError(error, 'Unable to download survey responses. Please try again.')
      setResponsesError(detail)
      message.error(detail)
    } finally {
      setExportingCsv(false)
    }
  }, [
    extractAdminError,
    formatAnswerValue,
    getAuthHeaders,
    responseLanguageFilter,
    responseStatusFilter,
  ])

  const openCreateModal = () => {
    setEditingQuestion(null)
    form.resetFields()
    form.setFieldsValue({
      prompt: '',
      description: undefined,
      type: 'shortText',
      options: undefined,
      isRequired: false,
      defaultValue: undefined,
      active: true,
      helpText: undefined,
    })
    setModalVisible(true)
  }

  const openEditModal = (question: SurveyQuestionRecord) => {
    setEditingQuestion(question)
    form.resetFields()
    form.setFieldsValue({
      prompt: question.prompt,
      description: question.description || undefined,
      type: question.type,
      options: question.options,
      isRequired: question.isRequired,
      defaultValue: coerceDefaultForForm(question.type, question.defaultValue),
      active: question.active,
      helpText: question.helpText || undefined,
    })
    setModalVisible(true)
  }

  const closeModal = () => {
    setModalVisible(false)
    setEditingQuestion(null)
    form.resetFields()
  }

  const openCopyModal = (question: SurveyQuestionRecord) => {
    setCopySourceQuestion(question)
    setCopySelectedLanguages([])
    setCopyModalVisible(true)
  }

  const closeCopyModal = () => {
    setCopyModalVisible(false)
    setCopySourceQuestion(null)
    setCopySelectedLanguages([])
  }

  const handleToggleCopyLanguage = (language: string, checked: boolean) => {
    setCopySelectedLanguages((prev) => {
      if (checked) {
        if (prev.includes(language)) {
          return prev
        }
        return [...prev, language]
      }
      return prev.filter((entry) => entry !== language)
    })
  }

  const handleCopySubmit = async () => {
    if (!copySourceQuestion || !currentLanguage) {
      setQuestionError('Unable to determine source language for copy.')
      message.error('Unable to determine source language for copy.')
      return
    }
    if (copySelectedLanguages.length === 0) {
      message.warning('Select at least one target language.')
      return
    }
    setCopySubmitting(true)
    setQuestionError(null)
    try {
      const headers = await getAuthHeaders()
      const { data } = await axios.post<{
        questionId: string
        copiedLanguages?: string[]
        skippedLanguages?: string[]
      }>(
        `${API_BASE}/surveys/language/admin/questions/${copySourceQuestion.questionId}/copy`,
        {
          sourceLanguage: currentLanguage,
          targetLanguages: copySelectedLanguages,
        },
        { headers }
      )

      const copied = data?.copiedLanguages || []
      const skipped = data?.skippedLanguages || []

      if (copied.length > 0) {
        const copiedNames = copied.map((lang) => getLanguageDisplayName(lang)).join(', ')
        message.success(`Copied question to: ${copiedNames}`)
      }
      if (skipped.length > 0) {
        const skippedNames = skipped.map((lang) => getLanguageDisplayName(lang)).join(', ')
        message.info(`Skipped (already existing): ${skippedNames}`)
      }

      closeCopyModal()
    } catch (error) {
      console.error('Failed to copy question', error)
      const detail = extractAdminError(error, 'Unable to copy question. Please try again.')
      setQuestionError(detail)
      message.error(detail)
    } finally {
      setCopySubmitting(false)
    }
  }

  const prepareQuestionPayload = (values: QuestionFormValues) => {
    const trimmedPrompt = values.prompt?.trim()
    if (!trimmedPrompt) {
      throw new Error('Prompt is required.')
    }

    const payload: any = {
      prompt: trimmedPrompt,
      type: values.type,
      isRequired: Boolean(values.isRequired),
      active: Boolean(values.active),
    }

    if (values.description && values.description.trim()) {
      payload.description = values.description.trim()
    }

    if (values.helpText && values.helpText.trim()) {
      payload.helpText = values.helpText.trim()
    }

    if (SELECT_TYPES.includes(values.type)) {
      const cleanedOptions = (values.options || [])
        .map((option) => option?.trim())
        .filter((option): option is string => Boolean(option))
      payload.options = cleanedOptions
    }

    const preparedDefault = prepareDefaultForPayload(values.type, values.defaultValue)
    if (preparedDefault !== undefined) {
      payload.defaultValue = preparedDefault
    }

    return payload
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      const questionPayload = prepareQuestionPayload(values)
      const headers = await getAuthHeaders()
      setSaving(true)
      setQuestionError(null)

      if (editingQuestion) {
        await axios.put(
          `${API_BASE}/surveys/language/admin/questions/${editingQuestion.questionId}`,
          {
            scope: currentScope,
            language: currentLanguage,
            question: questionPayload,
          },
          { headers }
        )
        message.success('Question updated')
      } else {
        await axios.post(
          `${API_BASE}/surveys/language/admin/questions`,
          {
            scope: currentScope,
            language: currentLanguage,
            question: questionPayload,
          },
          { headers }
        )
        message.success('Question created')
      }

      closeModal()
      fetchQuestions()
    } catch (error: any) {
      if (error?.errorFields) {
        return
      }
      console.error('Failed to save question', error)
      const detail = extractAdminError(error, 'Unable to save question')
      setQuestionError(detail)
      message.error(detail)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (questionId: string) => {
      try {
      setDeletingId(questionId)
      const headers = await getAuthHeaders()
      setQuestionError(null)
      await axios.delete(
        `${API_BASE}/surveys/language/admin/questions/${questionId}`,
        {
          headers,
          params: {
            scope: currentScope,
            language: currentLanguage,
          },
        }
      )
      message.success('Question deleted')
      fetchQuestions()
    } catch (error) {
      console.error('Failed to delete question', error)
      const detail = extractAdminError(error, 'Unable to delete question')
      setQuestionError(detail)
      message.error(detail)
    } finally {
      setDeletingId(null)
    }
  }

  const moveQuestion = async (index: number, direction: 'up' | 'down') => {
    if (reordering || questions.length < 2) {
      return
    }

    const targetIndex = direction === 'up' ? index - 1 : index + 1
    if (targetIndex < 0 || targetIndex >= questions.length) {
      return
    }

    const newOrder = [...questions]
    const [moved] = newOrder.splice(index, 1)
    newOrder.splice(targetIndex, 0, moved)

    try {
      setReordering(true)
      setQuestionError(null)
      const headers = await getAuthHeaders()
      await axios.patch(
        `${API_BASE}/surveys/language/admin/questions/order`,
        {
          scope: currentScope,
          language: currentLanguage,
          order: newOrder.map((item) => item.questionId),
        },
        { headers }
      )
      message.success('Question order updated')
      fetchQuestions()
    } catch (error) {
      console.error('Failed to update order', error)
      const detail = extractAdminError(error, 'Unable to update question order')
      setQuestionError(detail)
      message.error(detail)
    } finally {
      setReordering(false)
    }
  }

  const renderDefaultValue = (question: SurveyQuestionRecord) => {
    const { type, defaultValue } = question
    if (defaultValue === undefined || defaultValue === null || defaultValue === '') {
      return null
    }

    if (type === 'multiSelect' && Array.isArray(defaultValue)) {
      return (
        <span>
          Default:{' '}
          {defaultValue.map((value) => (
            <Tag key={value} color="blue" style={{ marginRight: 4 }}>
              {value}
            </Tag>
          ))}
        </span>
      )
    }

    if (type === 'boolean') {
      return <span>Default: {defaultValue ? 'True' : 'False'}</span>
    }

    return <span>Default: {String(defaultValue)}</span>
  }

  const renderAnswerValueNode = (value: any) => {
    const formatted = formatAnswerValue(value)
    if (formatted.includes('\n')) {
      return (
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
          {formatted}
        </pre>
      )
    }
    return <Text>{formatted}</Text>
  }

  const renderAnswerList = (answers: QuestionAnswer[]) => {
    if (!answers.length) {
      return <Text type="secondary">No answers provided.</Text>
    }
    return (
      <List
        size="small"
        dataSource={answers}
        renderItem={(item) => (
          <List.Item key={`${item.scope}-${item.language || 'general'}-${item.questionId}`}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Text strong>{item.prompt}</Text>
              {renderAnswerValueNode(item.answer)}
            </Space>
          </List.Item>
        )}
      />
    )
  }

  const typeValue = Form.useWatch('type', form)
const selectedIsSelect = SELECT_TYPES.includes(typeValue as QuestionType)

const questionTabContent = (
  <Space className="language-survey-admin-panel" direction="vertical" size={16}>
    <div className="language-survey-admin-toolbar">
      <Select
        value={selectedScope}
        className="language-survey-admin-select"
        onChange={handleScopeChange}
        showSearch
        optionFilterProp="children"
      >
        <Select.Option value="general">General Questions</Select.Option>
        <Select.Option value="language_common">Shared Language Questions</Select.Option>
        {allLanguages.map((language) => (
          <Select.Option key={language} value={language}>
            {getLanguageDisplayName(language)}
          </Select.Option>
        ))}
      </Select>
      <Tooltip title="Refresh">
        <Button icon={<ReloadOutlined />} onClick={fetchQuestions} disabled={loading} />
      </Tooltip>
      <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
        Add Question
      </Button>
    </div>

    <Alert
      type="info"
      showIcon
      message="General questions appear first. Shared language questions are asked for every selected language, followed by language-specific questions."
    />
    {questionError && (
      <Alert
        className="language-survey-admin-inline-alert"
        type="error"
        showIcon
        message="Question changes need attention"
        description={questionError}
        action={
          <Button size="small" onClick={fetchQuestions} disabled={loading}>
            Retry
          </Button>
        }
      />
    )}

    <Space className="language-survey-admin-section" direction="vertical" size={12}>
      <div className="language-survey-admin-summary-row">
        <Text strong>
          Scope:{' '}
          {currentScope === 'general'
            ? 'General (all languages)'
            : currentScope === 'language_common'
              ? 'Shared language questions'
              : `${currentLanguage} specific`}
        </Text>
        <Text type="secondary">
          {questions.length} question{questions.length === 1 ? '' : 's'}
        </Text>
        {updatedAt && (
          <Text type="secondary">Last updated: {new Date(updatedAt).toLocaleString()}</Text>
        )}
      </div>

      <Spin spinning={loading} tip="Loading questions...">
        {questions.length === 0 && !loading ? (
          <Empty description="No questions yet. Add your first question." />
        ) : (
          <List
            itemLayout="vertical"
            dataSource={questions}
            renderItem={(question, index) => (
              <List.Item
                key={question.questionId}
                actions={(() => {
                  const itemActions: React.ReactNode[] = [
                    <Tooltip key="move-up" title="Move up">
                      <Button
                        icon={<ArrowUpOutlined />}
                        size="small"
                        onClick={() => moveQuestion(index, 'up')}
                        disabled={index === 0 || reordering || loading || questions.length < 2}
                      />
                    </Tooltip>,
                    <Tooltip key="move-down" title="Move down">
                      <Button
                        icon={<ArrowDownOutlined />}
                        size="small"
                        onClick={() => moveQuestion(index, 'down')}
                        disabled={
                          index === questions.length - 1 ||
                          reordering ||
                          loading ||
                          questions.length < 2
                        }
                      />
                    </Tooltip>,
                    <Tooltip key="edit" title="Edit question">
                      <Button
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => openEditModal(question)}
                      />
                    </Tooltip>,
                  ]

                  if (currentScope === 'language' && currentLanguage) {
                    itemActions.push(
                      <Tooltip key="copy" title="Copy to other languages">
                        <Button
                          icon={<CopyOutlined />}
                          size="small"
                          onClick={() => openCopyModal(question)}
                        />
                      </Tooltip>
                    )
                  }

                  itemActions.push(
                    <Popconfirm
                      key="delete"
                      title="Delete this question?"
                      okText="Delete"
                      okType="danger"
                      onConfirm={() => handleDelete(question.questionId)}
                      okButtonProps={{ danger: true, loading: deletingId === question.questionId }}
                    >
                      <Button
                        icon={<DeleteOutlined />}
                        size="small"
                        danger
                        loading={deletingId === question.questionId}
                      />
                    </Popconfirm>
                  )

                  return itemActions
                })()}
              >
                <List.Item.Meta
                  title={
                    <Space size={8} wrap>
                      <Text strong>{index + 1}. {question.prompt}</Text>
                      <Tag color="blue">{question.type}</Tag>
                      {question.isRequired && <Tag color="red">Required</Tag>}
                      {!question.active && <Tag>Inactive</Tag>}
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size={6} style={{ width: '100%' }}>
                      {question.description && <Paragraph style={{ marginBottom: 0 }}>{question.description}</Paragraph>}
                      {question.helpText && (
                        <Text type="secondary">Help text: {question.helpText}</Text>
                      )}
                      {question.options && question.options.length > 0 && (
                        <div>
                          Options:{' '}
                          {question.options.map((option) => (
                            <Tag key={option} color="cyan" style={{ marginRight: 4 }}>
                              {option}
                            </Tag>
                          ))}
                        </div>
                      )}
                      {renderDefaultValue(question)}
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Spin>
    </Space>

    <Modal
      title={editingQuestion ? 'Edit Question' : 'Add Question'}
      open={modalVisible}
      okText={editingQuestion ? 'Save Changes' : 'Create Question'}
      onOk={handleSubmit}
      onCancel={closeModal}
      okButtonProps={{ loading: saving }}
      destroyOnClose
      width={720}
      className="language-survey-admin-modal"
    >
      <div className="language-survey-admin-modal-block">
        <h3>{editingQuestion ? 'Question details' : 'New question details'}</h3>
        <p>Define the author-facing prompt, answer type, options, and default state.</p>
      </div>
      <Form<QuestionFormValues> form={form} layout="vertical" preserve={false}>
        <Form.Item
          label="Prompt"
          name="prompt"
          rules={[{ required: true, message: 'Enter the question prompt' }]}
        >
          <Input.TextArea rows={3} placeholder="Enter the question authors should answer" />
        </Form.Item>

        <Form.Item label="Description" name="description">
          <Input.TextArea rows={2} placeholder="Optional description shown beneath the prompt" />
        </Form.Item>

        <Form.Item
          label="Question Type"
          name="type"
          rules={[{ required: true, message: 'Select a question type' }]}
        >
          <Select>
            <Select.Option value="shortText">Short Text</Select.Option>
            <Select.Option value="longText">Long Text</Select.Option>
            <Select.Option value="number">Number</Select.Option>
            <Select.Option value="rating">Rating</Select.Option>
            <Select.Option value="singleSelect">Single Select</Select.Option>
            <Select.Option value="multiSelect">Multi Select</Select.Option>
            <Select.Option value="boolean">Yes / No</Select.Option>
          </Select>
        </Form.Item>

        {selectedIsSelect && (
          <Form.Item label="Options" required>
            <Form.List
              name="options"
              rules={[{
                validator: async (_, value) => {
                  if (!value || value.length === 0) {
                    return Promise.reject(new Error('Add at least one option'))
                  }
                  if (value.some((entry: string) => !entry || !entry.trim())) {
                    return Promise.reject(new Error('Options cannot be empty'))
                  }
                  return Promise.resolve()
                },
              }]}
            >
              {(fields, { add, remove }, { errors }) => (
                <>
                  {fields.map((field, idx) => (
                    <Space key={field.key} align="baseline" style={{ marginBottom: 8 }}>
                      <Form.Item
                        {...field}
                        name={field.name}
                        fieldKey={field.fieldKey}
                        rules={[{ required: true, message: 'Option cannot be empty' }]}
                      >
                        <Input placeholder={`Option ${idx + 1}`} style={{ width: 320 }} />
                      </Form.Item>
                      <MinusCircleOutlined onClick={() => remove(field.name)} />
                    </Space>
                  ))}
                  <Form.ErrorList errors={errors} />
                  <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                    Add option
                  </Button>
                </>
              )}
            </Form.List>
          </Form.Item>
        )}

        <Divider />

        <Space size={16} wrap>
          <Form.Item name="isRequired" valuePropName="checked">
            <Checkbox>Required</Checkbox>
          </Form.Item>
          <Form.Item name="active" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" defaultChecked />
          </Form.Item>
        </Space>

        <Form.Item label="Help Text" name="helpText">
          <Input placeholder="Optional helper text shown to authors" />
        </Form.Item>

        <Form.Item label="Default Value" name="defaultValue">
          {(() => {
            switch (typeValue as QuestionType) {
              case 'boolean':
                return <Switch checkedChildren="Yes" unCheckedChildren="No" />
              case 'number':
              case 'rating':
                return <InputNumber style={{ width: '100%' }} />
              case 'singleSelect': {
                const options = (form.getFieldValue('options') || []).map((option: string) => ({
                  value: option,
                  label: option,
                }))
                return <Select options={options} allowClear placeholder="Select default option" />
              }
              case 'multiSelect': {
                const options = (form.getFieldValue('options') || []).map((option: string) => ({
                  value: option,
                  label: option,
                }))
                return (
                  <Select
                    options={options}
                    mode="multiple"
                    allowClear
                    placeholder="Select default options"
                  />
                )
              }
              default:
                return <Input placeholder="Optional default answer" />
            }
          })()}
        </Form.Item>
      </Form>
    </Modal>

    <Modal
      title={copySourceQuestion ? `Copy "${copySourceQuestion.prompt}"` : 'Copy Question'}
      open={copyModalVisible}
      onOk={handleCopySubmit}
      onCancel={closeCopyModal}
      okText="Copy"
      okButtonProps={{ loading: copySubmitting, disabled: copySelectedLanguages.length === 0 }}
      destroyOnClose
      className="language-survey-admin-modal"
    >
      <Space className="language-survey-admin-modal-block" direction="vertical" size={12}>
        <Text>Select the languages you want to copy this question to.</Text>
        <Space direction="vertical">
          {allLanguages.map((language) => {
            const isCurrent = language === currentLanguage
            const isChecked = isCurrent || copySelectedLanguages.includes(language)
            return (
              <Checkbox
                key={language}
                checked={isChecked}
                disabled={isCurrent}
                onChange={(event) => handleToggleCopyLanguage(language, event.target.checked)}
              >
                {getLanguageDisplayName(language)}
                {isCurrent && ' (current)'}
              </Checkbox>
            )
          })}
        </Space>
        {allLanguages.filter((language) => language !== currentLanguage).length === 0 && (
          <Text type="secondary">No other languages available to copy into.</Text>
        )}
      </Space>
    </Modal>
  </Space>
)

const responsesTabContent = (
  <Space className="language-survey-admin-panel" direction="vertical" size={16}>
    <div className="language-survey-admin-toolbar">
      <Select
        allowClear
        placeholder="Filter by language"
        className="language-survey-admin-select"
        value={responseLanguageFilter || undefined}
        onChange={handleResponseLanguageChange}
        showSearch
        optionFilterProp="children"
      >
        {allLanguages.map((language) => (
          <Select.Option key={language} value={language}>
            {getLanguageDisplayName(language)}
          </Select.Option>
        ))}
      </Select>
      <Select
        allowClear
        placeholder="Filter by status"
        className="language-survey-admin-select language-survey-admin-select--narrow"
        value={responseStatusFilter || undefined}
        onChange={handleResponseStatusChange}
      >
        <Select.Option value="completed">Completed</Select.Option>
        <Select.Option value="in_progress">In Progress</Select.Option>
      </Select>
      <Tooltip title="Refresh">
        <Button icon={<ReloadOutlined />} onClick={handleRefreshResponses} disabled={responsesLoading} />
      </Tooltip>
      <Button
        icon={<DownloadOutlined />}
        onClick={handleDownloadResponsesCsv}
        loading={exportingCsv}
        disabled={exportingCsv}
      >
        Download CSV
      </Button>
    </div>

    <Alert
      type="info"
      showIcon
      message="Select a response to review general, shared, and language-specific answers."
    />
    {responsesError && (
      <Alert
        className="language-survey-admin-inline-alert"
        type="error"
        showIcon
        message="Response data could not be refreshed"
        description={responsesError}
        action={
          <Button size="small" onClick={handleRefreshResponses} disabled={responsesLoading}>
            Retry
          </Button>
        }
      />
    )}

    <Table<SurveyResponseSummary>
      columns={responseColumns}
      dataSource={responses}
      rowKey="responseId"
      loading={responsesLoading}
      pagination={false}
      size="middle"
      scroll={{ x: true }}
      locale={{
        emptyText: responsesLoading ? 'Loading responses...' : 'No survey responses yet.',
      }}
      onRow={(record) => ({
        onClick: () => openResponseDetail(record.responseId),
        style: { cursor: 'pointer' },
      })}
    />

    {responsesNextCursor && (
      <div className="language-survey-admin-load-more">
        <Button
          icon={<ArrowDownOutlined />}
          onClick={handleLoadMoreResponses}
          disabled={!responsesNextCursor}
          loading={responsesLoading}
        >
          Load more
        </Button>
      </div>
    )}
  </Space>
)

return (
  <>
    <main className="language-survey-admin-page">
      <div className="language-survey-admin-shell">
        <section className="language-survey-admin-header" aria-labelledby="language-survey-admin-title">
          <div>
            <div className="language-survey-admin-kicker">Admin tools</div>
            <h1 id="language-survey-admin-title">Language Survey Admin</h1>
            <p>Manage survey questions, review responses, and export author feedback.</p>
          </div>
          <div className="language-survey-admin-header-actions">
            <Button size="large" onClick={() => navigate('/language-survey')}>
              Language Survey
            </Button>
          </div>
        </section>

        <section className="language-survey-admin-workspace">
          <Tabs
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key as 'questions' | 'responses')}
            items={[
              { key: 'questions', label: 'Question Sets', children: questionTabContent },
              { key: 'responses', label: 'Survey Responses', children: responsesTabContent },
            ]}
          />
        </section>
      </div>
    </main>

  <Drawer
    className="language-survey-admin-drawer"
    title={responseDetail?.summary.userId || 'Survey Response'}
    width={720}
    open={detailVisible}
    onClose={() => {
      setDetailVisible(false)
      setResponseDetail(null)
      setResponseDeletingId(null)
    }}
    destroyOnClose
  >
      {detailLoading ? (
        <Spin tip="Loading response..." />
      ) : detailError ? (
        <Alert
          className="language-survey-admin-inline-alert"
          type="error"
          showIcon
          message="Response detail could not be loaded"
          description={detailError}
        />
      ) : responseDetail ? (
        <Space className="language-survey-admin-drawer-content" direction="vertical" size={16}>
          <div className="language-survey-admin-drawer-actions">
            <Popconfirm
              title="Delete this survey response?"
              okText="Delete"
              okType="danger"
              onConfirm={() => handleDeleteResponse(responseDetail.summary.responseId)}
              okButtonProps={{
                danger: true,
                loading: responseDeletingId === responseDetail.summary.responseId,
              }}
              disabled={responseDeletingId === responseDetail.summary.responseId}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={responseDeletingId === responseDetail.summary.responseId}
              >
                Delete Response
              </Button>
            </Popconfirm>
          </div>
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="User">{responseDetail.summary.userId}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={responseDetail.summary.isComplete ? 'green' : 'orange'}>
                {(responseDetail.summary.status || (responseDetail.summary.isComplete ? 'completed' : 'in_progress')).toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Selected languages">
              {responseDetail.summary.selectedLanguages.length
                ? responseDetail.summary.selectedLanguages.join(', ')
                : 'None'}
            </Descriptions.Item>
            <Descriptions.Item label="Answered languages">
              {`${responseDetail.summary.answeredLanguages}/${responseDetail.summary.totalLanguages}`}
            </Descriptions.Item>
            <Descriptions.Item label="Completed languages">
              {`${responseDetail.summary.completedLanguages}/${responseDetail.summary.totalLanguages}`}
            </Descriptions.Item>
            <Descriptions.Item label="Started">
              {formatTimestamp(responseDetail.summary.startedAt)}
            </Descriptions.Item>
            <Descriptions.Item label="Updated">
              {formatTimestamp(responseDetail.summary.updatedAt)}
            </Descriptions.Item>
          </Descriptions>

          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Text strong>General questions</Text>
            {renderAnswerList(responseDetail.general)}
          </Space>

          {responseDetail.languages.map((languageBlock) => (
            <Space
              direction="vertical"
              size={12}
              style={{ width: '100%' }}
              key={languageBlock.language}
            >
              <Divider />
              <Space size={8} wrap>
                <Text strong>{getLanguageDisplayName(languageBlock.language)}</Text>
                <Tag color="blue">{languageBlock.answerCount}</Tag>
              </Space>
              {languageBlock.shared.length > 0 && (
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Text type="secondary">Shared questions</Text>
                  {renderAnswerList(languageBlock.shared)}
                </Space>
              )}
              {languageBlock.specific.length > 0 && (
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Text type="secondary">Language-specific questions</Text>
                  {renderAnswerList(languageBlock.specific)}
                </Space>
              )}
              {languageBlock.shared.length === 0 && languageBlock.specific.length === 0 && (
                <Text type="secondary">No answers recorded for this language.</Text>
              )}
            </Space>
          ))}
        </Space>
      ) : (
        <Empty description="Select a response to view details." />
      )}
    </Drawer>
  </>
)
}

export default LanguageSurveyAdmin
